/**
 * Reports frontend errors to the backend, which forwards them via email + WhatsApp.
 * Rate-limited on the backend (10 per minute per IP).
 * Silently swallows any failure — reporting must never break the app.
 */

export interface ReportErrorPayload {
    message: string;
    source: string;
    url?: string | null;
    component?: string | null;
    stack?: string | null;
}

let lastReportedMessage: string | null = null;

export function reportError({ message, source, url, component, stack }: ReportErrorPayload): void {
    // Deduplicate consecutive identical errors in the same session.
    if (message === lastReportedMessage) {
        return;
    }
    lastReportedMessage = message;

    const payload = {
        message: String(message).slice(0, 500),
        source,
        url: url || window.location.href,
        component: component || null,
        stack: stack ? String(stack).slice(0, 3000) : null,
    };

    // Use sendBeacon when available (works even during page unload).
    // Fall back to fetch for older browsers.
    const endpoint = '/api/client-error';
    const body = JSON.stringify(payload);
    const headers = { 'Content-Type': 'application/json' };

    try {
        if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: 'application/json' });
            navigator.sendBeacon(endpoint, blob);
        } else {
            fetch(endpoint, { method: 'POST', headers, body, keepalive: true }).catch(() => {});
        }
    } catch {
        // Never let the reporter crash the app.
    }
}
